/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { getImageBase64Sync } from '@src/slide/infrastructure/module/dependent/image/upload';
import { cloneDeep, cloneDeepWith } from '@tencent/slide-sdk-model/src/utils/clone-deep';
import ImageQuickLookManager from '../../../../infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { IMAGE_SRC_KEY, UNSET_KEY } from './cp-constants';
import CPUtils from './cp-utils';
import forIn from 'lodash/forIn';
import unset from 'lodash/unset';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { ISectionManager } from '@src/slide/application/service/section-manager/section-manager.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IEditorInstanceService } from '@src/slide/application/service/editor-instance-service/editor-instance-service.interface';
import { ShapePropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/shape-property-converter';
import { ITableDataModel, IShapeData } from '@tencent/slide-sdk-model';
import { imageCache } from '@tencent/alloy-graphics';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { CopiedKeyType, CopyDataVersion, ICopiedSdkData, ICopiedTableData } from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/paste-special.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { ENABLE_PASTE_SPECIAL } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { ICopySdkModelConfig } from '@src/slide/domain/ui-model/element-modeles/property/copy/copy-model/index.interface';

export interface SdkDataInheritItem {
    ownSdkData: IShapeData;
    inheritedSdkData: IShapeData;
}
export interface ITextBodyModel {
    type: DataModel.textBodyModel;
    globalPadId: string;
    data: SdkDataInheritItem[];
    sdkVersion: CopyDataVersion;
}

export interface ITextBodyModelVersion2 {
    type: DataModel.textBodyModel;
    globalPadId: string;
    data: [{
        // 2.0 老逻辑，命名是反的，sdkDataWithoutInherit 表示继承后的数据，sdkDataWithInherit 表示自身的数据
        sdkDataWithoutInherit: IShapeData;
        sdkDataWithInherit: IShapeData;
    }];
    sdkVersion: CopyDataVersion;
}

export interface ISdkModel {
    type: DataModel.sdkModel;
    globalPadId: string;
    data: any[];
    sdkVersion: CopyDataVersion;
    slideSize?: {
        width: number;
        height: number;
    };
}

export enum DataModel {
    textBodyModel,
    sdkModel
}

interface IStoreManagerServiceDelegates {
    selectionService: ISelectionService;
    uiDataService: IUiDataService;
    userPriviledgeService: IUserPriviledgeService;
    sectionManager: ISectionManager;
    editingStateManager: EditingStateManager;
    editorInstanceService: IEditorInstanceService;
}

class StoreManager {
    // 是否开启base64，会将数据中的cdn地址转换为base64
    public readonly useBase64Copy = !browser.isMobile;
    public base64UrlTempMap = new Map();
    public uiDataService?: IUiDataService;
    private selectionService?: ISelectionService;
    private sectionManager?: ISectionManager;
    private userPriviledgeService?: IUserPriviledgeService;
    private editingStateManager?: EditingStateManager;
    private editorInstanceService?: IEditorInstanceService;

    public get selectedElements(): Array<IElementBaseModel> {
        return this.selectionService?.selectedElements?.selectedArray || [];
    }

    public get selectedSlides(): Array<ISlideModel> {
        return this.selectionService?.selectedSlides?.selectedArray || [];
    }

    public get clearanceSelected(): boolean {
        return this.selectionService?.slideClearanceIndex !== -1;
    }

    /**
     * window.isEditing 是文本框或形状(含表格)的焦点
     * 解释权 by yunnanzhou
     */
    public get activePointInShape(): boolean {
        return !!this.editorInstanceService?.checkEditorIsEditing();
    }

    /**
     * editingStateManager.isEditing是其他输入框的焦点，如侧边栏
     * 解释权 by yunnanzhous
     */
    public get activePointInSideBar(): boolean {
        return !!this.editingStateManager?.isEditing;
    }

    public get lastRegion(): string {
        return this.selectionService?.lastMouseDownRegion || RegionType.NONE;
    }

    public get isEditorRegion(): boolean {
        return this.lastRegion === RegionType.EDITOR;
    }

    public get isNoteRegion(): boolean {
        return this.lastRegion === RegionType.NOTE;
    }

    public get isSlideListRegion(): boolean {
        return this.lastRegion === RegionType.SLIDELIST;
    }

    public get canEditAndCopy(): boolean {
        return !!this.userPriviledgeService
            && this.userPriviledgeService.pptEditorState < PPTEditorStateType.SELECTABLE;
    }

    public get canEdit(): boolean {
        return !!this.userPriviledgeService
            && this.userPriviledgeService.pptEditorState <= PPTEditorStateType.EDITABLE;
    }

    public setServiceDelegates(services: Partial<IStoreManagerServiceDelegates>) {
        this.uiDataService = this.uiDataService ?? services.uiDataService;
        this.selectionService = this.selectionService ?? services.selectionService;
        this.editingStateManager = this.editingStateManager ?? services.editingStateManager;
        this.editorInstanceService = this.editorInstanceService ?? services.editorInstanceService;
        this.sectionManager = this.sectionManager ?? services.sectionManager;
        this.userPriviledgeService = this.userPriviledgeService ?? services.userPriviledgeService;
    }

    public getTextSelectedAreaCopyData(uiData, shapeModel): ITextBodyModel {
        const { paragraphListWithInherit, paragraphListWithoutInherit } = uiData;

        // 这里理论都是直接调用ShapeConvert，之前直接调用shapeModel.constructor.convertDataUiToSdk可能是因为所有的文字都在shape下
        // 现在图表中的title，文本不在shapeModel下，在chartModel的下层节点上，如果直接使用chartModel的convert会转不出来
        // 所以这里直接调用ShapeModel来uiToSdk
        const inheritedSdkData = { nvPr: { txBox: true } } as any;
        const ownSdkData = { nvPr: { txBox: true } } as any;
        // eslint-disable-next-line max-len
        ShapePropertyConverter.convertPartialDataFromUiToSdk(paragraphListWithInherit, inheritedSdkData, shapeModel, false);
        // eslint-disable-next-line max-len
        ShapePropertyConverter.convertPartialDataFromUiToSdk(paragraphListWithoutInherit, ownSdkData, shapeModel, false);
        return {
            type: DataModel.textBodyModel,
            globalPadId: SlideDataServices.DocInfoManager()?.globalPadId ?? '',
            sdkVersion: CopyDataVersion.Version3,
            data: [
                {
                    ownSdkData,
                    inheritedSdkData,
                },
            ],
        };
    }

    /**
     * 获取插入幻灯片的间隙位置
     * 1.如果选中间隙则直接插入
     * 2.如果选中多个slide，插入最后一个slide后面
     * 3.兜底插入当前编辑页后面
     * @returns
     */
    public getInsertSlideIndex(): number {
        const { presentation } = this.uiDataService ?? {};
        const selectedSlides = this.selectionService?.selectedSlides.sortSelectedArray(
            presentation,
            this.selectionService?.selectedSlides.selectedArray,
        );
        let insertIndex = 0;
        if (this.clearanceSelected) {
            insertIndex = this.getConvertedSlideIndex(this.selectionService?.slideClearanceIndex ?? -1);
        } else if (selectedSlides?.length) {
            const lastSlide = selectedSlides[selectedSlides.length - 1];
            insertIndex = lastSlide.presentationRef.indexOfSlide(lastSlide) + 1;
        } else {
            if (!this.selectionService || this.selectionService.currentSlideType === UIModelType.MASTER) {
                insertIndex = 0;
            } else if (this.sectionManager && this.sectionManager.selectedSectionIndex !== -1) {
                insertIndex = ClearanceUtils.calcFirstSlideIndexFromSectionIndex(
                    this.sectionManager.selectedSectionIndex,
                    this.uiDataService?.presentation.sectionList ?? [],
                );
            } else {
                insertIndex = this.selectionService.currentSlideIndex + 1;
            }
        }
        return insertIndex;
    }

    // eslint-disable-next-line complexity
    public getInsertGapIndex(): number {
        const { presentation } = this.uiDataService ?? {};
        const selectedSlides = this.selectionService?.selectedSlides.sortSelectedArray(
            presentation,
            this.selectionService?.selectedSlides.selectedArray,
        );
        let insertIndex = 0;
        if (this.clearanceSelected) {
            insertIndex = this.selectionService?.slideClearanceIndex || 0;
        } else if (selectedSlides?.length) {
            const lastSlide = selectedSlides[selectedSlides.length - 1];
            const lastSlideIndex = lastSlide.presentationRef.indexOfSlide(lastSlide);
            const sectionList = this.uiDataService?.presentation.sectionList || [];
            if (sectionList.length) {
                // 存在节的情况下
                return ClearanceUtils.calcClearanceIndexFromSlideIndex(lastSlideIndex, sectionList);
            }
            // 不存在节的情况下
            return lastSlideIndex + 1;
        } else {
            if (!this.selectionService || this.selectionService.currentSlideType === UIModelType.MASTER) {
                insertIndex = 0;
            } else if (this.sectionManager && this.sectionManager.selectedSectionIndex !== -1) {
                insertIndex = ClearanceUtils.calcClearanceIndexFromSectionIndex(
                    this.sectionManager.selectedSectionIndex,
                    this.uiDataService?.presentation.sectionList ?? [],
                );
            } else {
                insertIndex = this.selectionService.currentSlideIndex + 1;
            }
        }
        return insertIndex;
    }

    /**
     * 同步获取选中的元素的拷贝数据
     * @returns
     */
    public getSelectedElementsCopyData(): ISdkModel {
        const selectedArray = this.getSelectedArray();
        if (!selectedArray.length) return {} as ISdkModel;
        const selectedSdkDataArray = selectedArray.map((element) => {
            const isSlide = element.type === UIModelType.SLIDE;
            const allSdkData = this.getCopiedSdkData(element, {
                cloneCustomizer: (value, key) => {
                    // 历史逻辑，修复本地打开图表字段丢失
                    UNSET_KEY.forEach((unsetKey) => {
                        if (value?.[unsetKey]) {
                            delete value[unsetKey];
                        }
                    });
                    // 图片上传未完成时复制，从 ImageQuickLookManager 里获取 base64,
                    if (IMAGE_SRC_KEY.includes(key) && this.isRelationId(value)) {
                        const quickLook = ImageQuickLookManager.getInstance();
                        const res = quickLook.getResourceByUrl(value);
                        return res?.getCdnUrl() || res?.getBase64Url();
                    }
                },
                // 选择性复制页的时候，由于有全套的母版主题数据，不用再携带继承数据
                withoutInheritedData: ENABLE_PASTE_SPECIAL && isSlide,
            });

            return ENABLE_PASTE_SPECIAL ? allSdkData : allSdkData[CopiedKeyType.INHERIT];
        });
        return this.prepareCopyData(selectedSdkDataArray);
    }

    /**
     * 获取选中元素的拷贝数据，将图片的地址转为base64
     * @returns
     */
    public getSelectedElementsCopyDataToBase64(): ISdkModel {
        const selectedArray = this.getSelectedArray();
        if (!selectedArray.length) return {} as ISdkModel;
        const imageCloneArr: Array<{
            target: object,
            key: string,
            valuePromise: Promise<string>
        }> = [];
        const selectedSdkDataArray = selectedArray.map((element) => {
            const allSdkData = this.getCopiedSdkData(element);
            const newSdkData = ENABLE_PASTE_SPECIAL ? allSdkData : allSdkData.inheritedSdkData;
            return cloneDeepWith(newSdkData, (value, key) => {
                // 如果子元素含有 imageSrcKey ，直接拷贝并保留引用
                if (IMAGE_SRC_KEY.some(key => value?.[key] !== undefined)) {
                    const copyItem = cloneDeep(value);
                    this.handlerIfChildHasImageKey(copyItem, imageCloneArr);
                    return copyItem;
                }
                UNSET_KEY.forEach(unsetKey => value?.[unsetKey] && delete value[unsetKey]);
            });
        });
        return this.prepareCopyData(selectedSdkDataArray);
    }

    public updateStore(): void {
    }

    public updateLastMouseDownRegion(region: string): void {
        if (this.selectionService) {
            this.selectionService.lastMouseDownRegion = region;
        }
    }

    /**
     * 获取当前选中的元素，可能是shape或者slide
     * @returns
     */
    public getSelectedArray() {
        if (!this.uiDataService) return [];
        let selectedArray: any[] = [];
        if (!browser.isMobile && this.sectionManager) {
            const { selectedSectionIndex } = this.sectionManager;
            if (selectedSectionIndex !== -1) {
                const selectedSectionModel = this.uiDataService
                    .presentation.getSectionProperty().getSectionByIndex(selectedSectionIndex);
                selectedSectionModel && selectedArray.push(selectedSectionModel);
            }
        }
        const selectedSlides = this.selectionService?.selectedSlides;
        const selectedElements = this.selectionService?.selectedElements;
        selectedArray = selectedArray.concat(...(this.isSlideListRegion
            ? this.selectionService
                ?.selectedSlides.sortSelectedArray(this.uiDataService.presentation, selectedSlides?.selectedArray)
            : selectedElements?.getSortedSelectedArray()) || []);
        return selectedArray;
    }

    /**
     * 遍历复制项，将 promise 存入预先准备的数组中，方便后续处理
     * @param copyItem
     * @param imageCloneArr
     */
    private handlerIfChildHasImageKey(copyItem, imageCloneArr) {
        forIn(copyItem, (value, key, object) => {
            UNSET_KEY.forEach(unsetKey => unset(value, unsetKey));
            // 图片上传未完成时复制，从 ImageQuickLookManager 里获取 base64,
            if (IMAGE_SRC_KEY.includes(key)) {
                if (this.isRelationId(value)) {
                    const quickLook = ImageQuickLookManager.getInstance();
                    const res = quickLook.getResourceByUrl(value);
                    if (res) {
                        object[key] = res.getCdnUrl() ? res.getCdnUrl() : res.getBase64Url();
                    }
                    return;
                }
                if (!this.base64UrlTempMap.get(value)) {
                    const image = imageCache.get({ alloyParams: { urlOrImage: value } });
                    if (image?.isComplete) {
                        this.base64UrlTempMap.set(value, getImageBase64Sync(image));
                    }
                }
                imageCloneArr.push({ target: copyItem, key });
            }
        });
    }

    /**
     * 获取复制后的sdkData，表格选中态需要特殊处理
     * @param element
     * @returns
     */
    private getCopiedSdkData(element, config?: ICopySdkModelConfig): ICopiedSdkData {
        if (element.type === UIModelType.TABLE) {
            return this.getCopiedTableSdkData(element);
        }
        return element.copyModelHandler().getCopiedSdkModel(config);
    }

    private prepareCopyData(selectedSdkDataArray): ISdkModel {
        // 剪切板的data type 0 为sdk model，1为sdk 文字片段信息 textBody
        const copyData: ISdkModel = {
            type: DataModel.sdkModel,
            data: selectedSdkDataArray,
            globalPadId: SlideDataServices.DocInfoManager()?.globalPadId ?? '',
            sdkVersion: CopyDataVersion.Version3,
        };
        if (this.isSlideListRegion) {
            // 如果拷贝的是 slide，需要带上宽高，用于缩放比
            const { cx: width = 12192000, cy: height = 6858000 } = SlideDataServices.SDKDataServices
                .PresentationStoreService()?.getPresentation().props.sldSz || {};
            copyData.slideSize = {
                width,
                height,
            };
        }
        return copyData;
    }

    /**
     * 获取已复制的table数据
     * @param elementModel ITableModel
     * @returns ICopiedTableData
     */
    private getCopiedTableSdkData(elementModel: ITableModel): ICopiedTableData {
        const tableDataModel = elementModel.sdkData as unknown as ITableDataModel;
        const inheritedSdkData = this.handlerTableSdkData(tableDataModel.getInheritedData(), elementModel);
        const ownSdkData = this.handlerTableSdkData(tableDataModel.getOwnedData(), elementModel);
        return { inheritedSdkData, ownSdkData };
    }

    private handlerTableSdkData(elementSdkData, elementModel) {
        const { selectedRange, selectedCellRanges } = elementModel;
        const extractData = { ...cloneDeep(elementSdkData), selectedRange, selectedCellRanges };
        CPUtils.extractTblRangeData(extractData);
        delete extractData.selectedRange;
        delete extractData.selectedCellRanges;
        // 插入的表格自动替换placeholder时，会加上标记，删除时自动还原placeholder。剪切时无需还原，需要清除标记
        if (extractData.nvPr?.ph) {
            delete extractData.nvPr.ph;
        }
        return extractData;
    }

    private getConvertedSlideIndex(clearanceIndex: number): number {
        if (this.uiDataService?.presentation.hasSections()) {
            return ClearanceUtils.calcSlideIndexFromClearanceIndex(
                clearanceIndex,
                this.uiDataService.presentation.sectionList || [],
            );
        }
        return clearanceIndex;
    }

    private isRelationId(url: string | undefined) {
        return !!url?.startsWith('rel:') || !!url?.startsWith('pptx:');
    }
}

export default StoreManager;
